import { useState } from 'react'
import type { DatabaseConnection } from '@/stores/connectionStore'
import { useToast } from '@/hooks/useToast'
import { AgentProxyService } from '@/services/agentProxyService'
import { useQueryClient } from '@tanstack/react-query'
import { databaseKeys } from '@/hooks/useDatabase'
import {
  InformationCircleIcon
} from '@heroicons/react/24/outline'

interface CreateSequenceViewProps {
  connection: DatabaseConnection
  database: string
  schema?: string
  onSequenceCreated: () => void
}

export function CreateSequenceView({ connection, database, schema, onSequenceCreated }: CreateSequenceViewProps) {
  const [sequenceName, setSequenceName] = useState('')
  const [startValue, setStartValue] = useState(1)
  const [incrementBy, setIncrementBy] = useState(1)
  const [minValue, setMinValue] = useState<number | null>(null)
  const [maxValue, setMaxValue] = useState<number | null>(null)
  const [cache, setCache] = useState(1)
  const [cycle, setCycle] = useState(false)
  const [ownedBy, setOwnedBy] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  
  const { showToast } = useToast()
  const queryClient = useQueryClient()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!sequenceName.trim()) {
      showToast('error', 'Please enter a sequence name')
      return
    }

    setIsSubmitting(true)
    
    try {
      // Create sequence using agent-proxy
      await AgentProxyService.createSequence(
        connection.id,
        database,
        {
          sequence_name: sequenceName,
          schema_name: schema,
          start_value: startValue,
          increment_by: incrementBy,
          min_value: minValue,
          max_value: maxValue,
          cache_size: cache,
          cycle: cycle,
          owned_by: ownedBy || null
        }
      )
      
      showToast('success', `Sequence "${sequenceName}" created successfully`)
      
      // Invalidate sequences queries to refresh UI
      await queryClient.invalidateQueries({ 
        queryKey: [...databaseKeys.all, 'sequences', connection.id, database, schema] 
      })
      
      onSequenceCreated()
    } catch (error) {
      console.error('Failed to create sequence:', error)
      const errorMessage = error instanceof Error ? error.message : 'Failed to create sequence'
      showToast('error', errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  // Only PostgreSQL supports sequences
  if (connection.type !== 'postgresql') {
    return (
      <div className="h-full flex flex-col bg-white dark:bg-gray-900">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="text-6xl mb-4">🔢</div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Sequences Not Supported
            </h2>
            <p className="text-gray-500 dark:text-gray-400">
              Sequences are only available in PostgreSQL databases.
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col bg-white dark:bg-gray-900">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center">
          <div className="text-2xl mr-3">🔢</div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Create Sequence
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Create a new PostgreSQL sequence for generating unique numeric values
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-4xl mx-auto">
          <form onSubmit={(e) => void handleSubmit(e)} className="space-y-6">
            {/* Basic Information */}
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Basic Information
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Sequence Name *
                  </label>
                  <input
                    type="text"
                    value={sequenceName}
                    onChange={(e) => { setSequenceName(e.target.value) }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="seq_example_name"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Sequence Parameters */}
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Sequence Parameters
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Start Value
                  </label>
                  <input
                    type="number"
                    value={startValue}
                    onChange={(e) => { setStartValue(Number.parseInt(e.target.value) || 1) }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    The first value the sequence will generate
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Increment By
                  </label>
                  <input
                    type="number"
                    value={incrementBy}
                    onChange={(e) => { setIncrementBy(Number.parseInt(e.target.value) || 1) }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    The step size for each nextval() call
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Minimum Value
                  </label>
                  <input
                    type="number"
                    value={minValue || ''}
                    onChange={(e) => { setMinValue(e.target.value ? Number.parseInt(e.target.value) : null) }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="No minimum"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Optional: Minimum value the sequence can generate
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Maximum Value
                  </label>
                  <input
                    type="number"
                    value={maxValue || ''}
                    onChange={(e) => { setMaxValue(e.target.value ? Number.parseInt(e.target.value) : null) }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="No maximum"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Optional: Maximum value the sequence can generate
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Cache
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={cache}
                    onChange={(e) => { setCache(Number.parseInt(e.target.value) || 1) }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Number of values to pre-allocate for performance
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Owned By (Optional)
                  </label>
                  <input
                    type="text"
                    value={ownedBy}
                    onChange={(e) => { setOwnedBy(e.target.value) }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="table.column"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Associate sequence with a table column (e.g., users.id)
                  </p>
                </div>
              </div>

              <div className="mt-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={cycle}
                    onChange={(e) => { setCycle(e.target.checked) }}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-600 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                    Cycle when reaching min/max values
                  </span>
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-6">
                  When enabled, the sequence will wrap around when it reaches its minimum or maximum value
                </p>
              </div>
            </div>

            {/* Info Box */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <div className="flex items-start">
                <InformationCircleIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 mr-3 flex-shrink-0" />
                <div className="text-sm text-blue-800 dark:text-blue-200">
                  <p className="font-medium mb-1">Sequence Usage:</p>
                  <ul className="list-disc list-inside space-y-1 text-xs">
                    <li>Use <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">nextval('sequence_name')</code> to get the next value</li>
                    <li>Use <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">currval('sequence_name')</code> to get the current value</li>
                    <li>Sequences are commonly used for auto-incrementing primary keys</li>
                    <li>Higher cache values improve performance but may create gaps in sequences</li>
                    <li>Owned sequences are automatically dropped when the owning column is dropped</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-6 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={onSequenceCreated}
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isSubmitting ? 'Creating...' : 'Create Sequence'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
